import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import set from 'lodash/set';
import {
  authUserId,
  getOrganisationId,
  isBroker,
  isBrokerOrganisation,
} from '../../auth/redux/authActions';
import getIncludedResource from '../../../helpers/getIncludedResource';
import uuid from 'uuid/v4';
import { actions as productActions } from '../../product/redux/productActions';
import SmartLuggageForm from '../components/SmartLuggageForm/SmartLuggageForm';
import { openMenu } from '../../layout/redux/sideNavAction';
import { addDays, formatISO } from 'date-fns';

const SmartLuggageFormContainer = ({
  application,
  currentProduct,
  source,
  brokerId,
  channelId,
  isBrokerChannel,
  policyId,
  productId,
  isMta,
  product,
  isCloned,
  user,
  customer,
  getSingleInsurerQuotes,
  getSingleInsurerMta,
  updateApplication,
  saveApplication,
  updateApplicationAndRedirect,
  saveApplicationAndRedirect,
  updateApplicationEmailAndRedirect,
  saveApplicationEmailAndRedirect,
  redirectAfterSuccessfulCardPayment,
  issuePolicy,
  prepare,
  openMenu,
}) => {
  const [premiums, setPremiums] = useState([]);

  useEffect(
    () => {
      openMenu(false);
    },
    [openMenu],
  );

  const getNewInitialValues = useMemo(
    () => (
      sourceId,
      newCustomer,
      newProductId,
      newUser,
      brokerId,
      channelId,
      isBrokerChannel,
    ) => {
      const currentCustomerAttributes = get(newCustomer, 'data.attributes');
      const travellers = [
        {
          title: get(currentCustomerAttributes, 'title', ''),
          first_name: get(currentCustomerAttributes, 'first_name', ''),
          last_name: get(currentCustomerAttributes, 'last_name', ''),
          email: get(currentCustomerAttributes, 'email', ''),

          phone1: get(currentCustomerAttributes, 'phone1', ''),
          phone2: get(currentCustomerAttributes, 'phone2', ''),
          marketing_options: get(currentCustomerAttributes, 'marketing_options', {
            post: -1,
            email: -1,
            sms: -1,
            phone: -1,
          }),
          sanction_id: get(currentCustomerAttributes, 'sanction_id', uuid()),
          sanctions_check: get(currentCustomerAttributes, 'sanctions_check', []),
          address: {
            line1: get(currentCustomerAttributes, 'address.line1', ''),
            line2: get(currentCustomerAttributes, 'address.line2', ''),
            line3: get(currentCustomerAttributes, 'address.line3', ''),
            town: get(currentCustomerAttributes, 'address.town', ''),
            county: get(currentCustomerAttributes, 'address.county', ''),
            postcode: get(currentCustomerAttributes, 'address.postcode', ''),
          },
        },
      ];
      return {
        data: {
          current_tab: 1,
          type: 'application',
          attributes: {
            quote_reference: '',
            quote_type: '',
            metadata: {
              communications: {
                email: !isBroker() && !isBrokerOrganisation(),
              },
              num_travellers: undefined,
              send_email: false,
              generate_documents: false,
              scheme_type: '',
              residency_declaration: undefined,
              start_date: '',
              end_date: '',
              travellers: travellers,
              source: '',
              renewals_enabled: false,
              storage: {
                quote_originator: get(newUser, 'data.attributes'),
                quote_originator_organisation_id: getOrganisationId(),
              },
              overrides: null,
              campaign: '',
              sales_channel: isBrokerChannel ? 'jtc-b2b' : '',
            },
          },
          relationships: {
            broker: {
              data: {
                id: brokerId,
              },
            },
            channel: {
              data: {
                id: channelId,
              },
            },
            source: {
              data: {
                // id: brokerSource ? brokerSource.id : null
                id: sourceId,
              },
            },
            schemes: {
              data: [],
            },
            user: {
              data: {
                id: authUserId(),
              },
            },
            customer: {
              data: {
                id: newCustomer ? newCustomer.data.id : '',
              },
            },
            product: {
              data: {
                id: newProductId,
              },
            },
          },
        },
      };
      // };
    },
    [],
  );

  const getApplicationInitialValues = useMemo(
    () => (existingApplication, existingUser, isCloned, isMta, productId) => {
      if (!existingApplication) {
        return null;
      }
      const overrides = get(existingApplication, 'data.attributes.metadata.overrides');
      if (Array.isArray(overrides) && overrides.length === 0) {
        set(existingApplication, 'data.attributes.metadata.overrides', null);
      }

      // Reset any policy details if cloning
      if (existingApplication && isCloned) {
        set(existingApplication, 'data.id', null);
        set(existingApplication, 'data.attributes.policy_number', '');
        set(existingApplication, 'data.attributes.quote_reference', '');
        set(existingApplication, 'data.relationships.schemes.data', []);
        set(
          existingApplication,
          'data.attributes.metadata.storage.quote_originator',
          get(existingUser, 'data.attributes'),
        );
        set(existingApplication, 'data.relationships.product.data.id', productId);
      }

      if (
        get(existingApplication, 'data.attributes.metadata.send_sms') &&
        !get(existingApplication, 'data.attributes.metadata.adults[0].phone2', false)
      ) {
        set(existingApplication, 'data.attributes.metadata.send_sms', false);
      }

      if (
        !isMta &&
        get(existingApplication, 'data.relationships.schemes.data', []).length > 1
      ) {
        const channel = getIncludedResource(
          existingApplication.data,
          existingApplication.included,
          'channel',
        );
        if (get(channel, 'data.attributes.channel_type') === 'B2C') {
          set(existingApplication, 'data.relationships.schemes.data', []);
        }
      }

      if (
        existingApplication &&
        get(existingApplication, 'data.attributes.metadata.storage.quote_originator', [])
          .length < 1 &&
        (get(existingApplication, 'data.attributes.status') === 'renewal invited' ||
          get(existingApplication, 'data.attributes.status') === 'renewal')
      ) {
        // update quote originator on renewals to whomever first edits the quote
        set(
          existingApplication,
          'data.attributes.metadata.storage.quote_originator',
          get(existingUser, 'data.attributes'),
        );
      }

      return existingApplication;
    },
    [],
  );

  const getQuotes = (values) => {
    const toApi = {
      data: {
        ...values.data,
        attributes: {
          ...values.data.attributes,
          metadata: {
            ...values.data.attributes.metadata,
            start_date:
              values?.data.attributes.metadata?.start_date ||
              formatISO(addDays(new Date(), 1)),
          },
        },
      },
    };

    return Promise.resolve(getSingleInsurerQuotes(source.id, toApi))
      .then((quotes) => {
        const premiums = Object.values(quotes);

        setPremiums(premiums);
        return premiums;
      })
      .catch(() => {
        setPremiums([]);
      });
  };

  const calculate = (values) => {
    if (isMta) {
      return getMtaQuotes(values);
    }
    return getQuotes(values);
  };

  const getMtaQuotes = (values) => {
    return Promise.resolve(getSingleInsurerMta(policyId, values))
      .then((quotes) => {
        const premiums = Object.values(quotes);

        setPremiums(premiums);
      })
      .catch(() => {
        setPremiums([]);
      });
  };

  const save = (values, calculate) => {
    if (values.data.id && !isMta) {
      return updateApplication(
        values,
        currentProduct,
        values.data.id,
        null,
        isMta,
        calculate,
      );
    }
    return saveApplication(values, null, null, isMta, calculate);
  };

  const saveAndExit = (values) => {
    const hasSchemes = !!values.data.relationships.schemes.data.length;
    set(values, 'data.attributes.metadata.generate_documents', true);
    if (values?.data?.id && !isMta) {
      return updateApplicationAndRedirect(
        values,
        currentProduct,
        values.data.id,
        hasSchemes,
      );
    }
    return saveApplicationAndRedirect(values, currentProduct, null, hasSchemes);
  };

  const saveEmailAndExit = (values) => {
    const hasSchemes = !!values.data.relationships.schemes.data.length;
    if (values.data.id) {
      return updateApplicationEmailAndRedirect(values, null, values.data.id, hasSchemes);
    }
    return saveApplicationEmailAndRedirect(values, null, null, hasSchemes);
  };

  const issue = (values) => {
    const paymentType = values?.meta?.payment_type;
    if (values.data.id) {
      return Promise.resolve(
        updateApplication(values, null, values.data.id, null, isMta, true),
      ).then((resp) => {
        set(
          values,
          'data.relationships.premium.data.id',
          get(resp, 'data.relationships.premiums.data.0.id'),
        );

        if (paymentType === 'card') {
          return prepare(
            values.data.id,
            get(resp, 'data.relationships.premiums.data.0.id'),
          );
        } else {
          return issuePolicy(values.data.id, values);
        }
      });
    }
    return Promise.resolve(saveApplication(values, null, null, isMta, false)).then(
      (resp) => {
        if (paymentType === 'card') {
          return prepare(
            values.data.id,
            get(resp, 'data.relationships.premiums.data.0.id'),
          );
        } else {
          return issuePolicy(values.data.id, values);
        }
      },
    );
  };

  if (!source && !isMta) {
    return <div />;
  }

  const redirectToIssued = (policyId) => {
    redirectAfterSuccessfulCardPayment(policyId);
  };

  const initialValues =
    getApplicationInitialValues(application, user, isCloned, isMta, productId) ||
    getNewInitialValues(
      source?.id,
      customer,
      productId,
      user,
      brokerId,
      channelId,
      isBrokerChannel,
    );
  if (isMta && !initialValues?.data?.attributes?.policy_number) {
    return <div />;
  }

  const channelType = isBroker() ? 'B2B' : 'DIR';
  const productRules = currentProduct?.data.attributes.metadata.rules;
  return (
    <div>
      <SmartLuggageForm
        calculatedPremiums={premiums}
        channelType={channelType}
        definitions={get(currentProduct, 'data.attributes.metadata.definitions.b2b', {})}
        resource={initialValues}
        productName={currentProduct.data.attributes.name}
        productCode={currentProduct.data.attributes.product_code}
        productId={productId}
        productRules={productRules}
        purchaseDeclaration={
          currentProduct.data.attributes.metadata.purchase_declaration[channelType]
        }
        source={source}
        isCalculating={product.isCalculating}
        isSaving={product.isSaving}
        isMta={isMta}
        onSave={save}
        onIssue={issue}
        onCalculate={calculate}
        onSaveAndExit={saveAndExit}
        onSaveEmailAndExit={saveEmailAndExit}
        onRedirectToIssued={redirectToIssued}
      />
    </div>
  );
};

export default connect(
  (state) => ({
    product: state.product,
    brokerState: state.broker,
  }),
  {
    getSingleInsurerQuotes: productActions.getSingleInsurerQuotes,
    getSingleInsurerMta: productActions.getSingleInsurerMta,
    issuePolicy: productActions.issuePolicy,
    prepare: productActions.prepareApplication,
    saveApplication: productActions.saveApplication,
    updateApplication: productActions.updateApplication,
    saveApplicationEmailAndRedirect: productActions.saveApplicationEmailAndRedirect,
    updateApplicationEmailAndRedirect: productActions.updateApplicationEmailAndRedirect,
    updateApplicationAndRedirect: productActions.updateApplicationAndRedirect,
    saveApplicationAndRedirect: productActions.saveApplicationAndRedirect,
    showPaymentIframe: productActions.showPaymentIframe,
    redirectAfterSuccessfulCardPayment: productActions.redirectAfterSuccessfulCardPayment,
    openMenu: openMenu,
  },
)(SmartLuggageFormContainer);
